import { createApi } from '@reduxjs/toolkit/dist/query/react'
import baseQueryWithOnlineCourse from '../../app/baseQueryWithOnlineCourse'
import {
    VideoLessonState,
    WatchedVideoLessonTimeState,
    WatchedVideoLessonState,
    StartWatchVideoLessonState,
    TopicVideoPercentageState,
} from '../types'

interface getStudentVideoLessonAnalysisParams {
    courseTopicId: number
    studentId: number
}

interface getStudentWatchedVideoTimeAnalysisParams {
    videoLessonId: number
    studentId: number
}

interface updatePlayingTimeParams {
    watchedVideoDataId: number
    timeAt: number
}

export const videoLessonApiSlice = createApi({
    reducerPath: 'videoLessonApi',
    baseQuery: baseQueryWithOnlineCourse,
    tagTypes: ['VideoLesson', 'WatchedTime'],
    endpoints: (build) => ({
        createVideoLesson: build.mutation<void, VideoLessonState>({
            query: (videoLesson) => ({
                url: 'video-lesson',
                method: 'POST',
                body: videoLesson,
            }),
            invalidatesTags: [{ type: 'VideoLesson', id: 'LIST' }],
        }),

        updateVideoLesson: build.mutation<void, VideoLessonState>({
            query: (videoLesson) => ({
                url: `video-lesson/${videoLesson.id}`,
                method: 'PUT',
                body: videoLesson,
            }),
            invalidatesTags: [{ type: 'VideoLesson', id: 'LIST' }],
        }),

        deleteVideoLesson: build.mutation<
            { success: boolean; id: number },
            number
        >({
            query: (id) => ({
                url: `/video-lesson/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, id) => [
                { type: 'VideoLesson', id },
            ],
        }),

        startWatchingVideoLesson: build.mutation<
            StartWatchVideoLessonState,
            StartWatchVideoLessonState
        >({
            query: (params) => ({
                url: 'watched-video/create',
                method: 'POST',
                body: params,
            }),
            transformResponse: (response: any) => response.data,
        }),

        updatePlayingTime: build.mutation<
            TopicVideoPercentageState,
            updatePlayingTimeParams
        >({
            query: (params) => ({
                url: 'watched-video/update',
                method: 'POST',
                body: params,
            }),
            transformResponse: (response: any) => response.data,
        }),

        getStudentVideoLessonAnalysis: build.query<
            WatchedVideoLessonState[],
            getStudentVideoLessonAnalysisParams
        >({
            query: ({ courseTopicId, studentId }) => ({
                url: `watched-video/student-analysis/course-topic/${courseTopicId}`,
                params: {
                    studentId,
                },
                providesTags: (result) =>
                    result
                        ? [
                              ...result.map(({ videoLessonId }) => ({
                                  type: 'VideoLesson' as const,
                                  videoLessonId,
                              })),
                              { type: 'VideoLesson', id: 'LIST' },
                          ]
                        : [{ type: 'VideoLesson', id: 'LIST' }],
            }),
            transformResponse: (response: any) => response.data,
        }),

        getStudentWatchedVideoTimeAnalysis: build.query<
            WatchedVideoLessonTimeState[],
            getStudentWatchedVideoTimeAnalysisParams
        >({
            query: ({ videoLessonId, studentId }) => ({
                url: `watched-video/student-analysis/video-data/${videoLessonId}`,
                params: {
                    studentId,
                },
                providesTags: (result) =>
                    result
                        ? [
                              ...result.map(({ videoLessonId }) => ({
                                  type: 'WatchedTime' as const,
                                  videoLessonId,
                              })),
                              { type: 'WatchedTime', id: 'LIST' },
                          ]
                        : [{ type: 'WatchedTime', id: 'LIST' }],
            }),
            transformResponse: (response: any) => response.data,
        }),
    }),
})
